// "Put back" on one pull line: a popover hung off the key that asked, with a
// quantity that starts at everything still out and can never exceed it.

import React, { useState } from 'react';
import { buttonClass } from '../../components/ui/Button';
import KeyPopover from './KeyPopover';
import { useToast } from '../../components/Toast/ToastContext';
import { itemLabel } from '../../components/ItemName';
import { Alert } from '../../components/ui/Alert';
import Reveal from '../../components/ui/Reveal';
import { Field } from '../../components/ui/Field';
import { usePullsApi, type PullLine } from './pullsApi';

export const PUT_BACK_KEY =
  'whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface disabled:opacity-40 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors duration-150 ease-emphasized motion-reduce:transition-none';

interface PutBackFormProps {
  line: PullLine;
  close: () => void;
  onDone: () => void;
}

const PutBackForm: React.FC<PutBackFormProps> = ({ line, close, onDone }) => {
  const api = usePullsApi();
  const toast = useToast();
  const [qty, setQty] = useState(String(line.remaining));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = Number(qty);
  const valid = Number.isInteger(n) && n >= 1 && n <= line.remaining;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.putBack(line.transaction_id, n);
      toast.success(`Put back ${n} ${itemLabel(line)}`);
      close();
      onDone();
    } catch (err: any) {
      setError(err?.message || 'The put-back did not go through. Try again.');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <label className="block">
        <span className="mb-1 block text-xs text-on-surface-variant">
          Quantity (up to {line.remaining})
        </span>
        <Field
          type="number"
          inputMode="numeric"
          min={1}
          max={line.remaining}
          step={1}
          value={qty}
          onChange={(e) => {
            const raw = e.target.value;
            // A ceiling at what is still out: typing past it lands on it.
            setQty(raw !== '' && Number(raw) > line.remaining ? String(line.remaining) : raw);
            setError(null);
          }}
          aria-label="Quantity to put back"
          aria-invalid={!valid && qty !== ''}
          className="m-0"
        />
      </label>
      <Reveal show={!!error}>
        <Alert tone="error" variant="text">
          {error}
        </Alert>
      </Reveal>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={close}
          className={buttonClass('quiet')}
        >
          Never mind
        </button>
        <button
          type="submit"
          disabled={!valid || busy}
          className={buttonClass('filled')}
        >
          {busy ? 'Putting back…' : 'Put back'}
        </button>
      </div>
    </form>
  );
};

interface Props {
  line: PullLine;
  onDone: () => void;
}

const PutBackPopover: React.FC<Props> = ({ line, onDone }) => (
  <KeyPopover
    triggerLabel="Put back"
    triggerAriaLabel={`Put back ${itemLabel(line)}`}
    triggerClassName={PUT_BACK_KEY}
    tooltip="Return these units to the shelf they came from"
    title="Put back"
    disabled={line.remaining < 1}
    align="end"
    width="sm"
  >
    {(close) => <PutBackForm line={line} close={close} onDone={onDone} />}
  </KeyPopover>
);

export default PutBackPopover;
